import { ExpandableCard } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getLinkPath } from "../lookup/links";
import { GlobalSearchResult, IncompleteItemsSearchFilters } from "@/api/williams/search";
import { highlight } from "@/util/Highlighter";
import { Link } from "react-router-dom";
import { String } from "@/util";
import { useIncompleteItems } from "../hooks";
import { useState } from "react";

export type IncompleteItemsProps = {
  filters: Partial<IncompleteItemsSearchFilters>;
  expanded?: boolean;
};

export function IncompleteItems({ filters, ...props }: IncompleteItemsProps) {
  const items = useIncompleteItems(filters);

  return (
    <ExpandableCard caption={`Incomplete (${items.data?.results.length ?? 0})`} expanded={props.expanded}>
      {items.isLoading ? (
        <p>Loading...</p>
      ) : items.isError ? (
        <p>An unknown error occurred.</p>
      ) : items.isSuccess && items.data.results.length > 0 ? (
        <IncompleteItemsTable data={items.data.results} search={filters.searchString} />
      ) : filters.searchString ? (
        <p>Your search returned no results.</p>
      ) : (
        <p>No incomplete items found.</p>
      )}
    </ExpandableCard>
  );
}

function IncompleteItemsTable({ data = [], search }: { data: GlobalSearchResult[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless table-fixed mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th className="d-none d-md-table-cell">Type</th>
          <th className="d-none d-md-table-cell">Description</th>
          <th className="d-table-cell d-md-none" />
        </tr>
      </thead>
      <tbody>
        {data.map((result) => (
          <IncompleteItem key={result.id} result={result} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function IncompleteItem({ result, search }: { result: GlobalSearchResult; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <>
      <tr>
        <td>
          <Link to={getLinkPath(result)} className="link link-primary">
            {highlight(result.name, search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">{result.resultType}</td>
        <td className="d-none d-md-table-cell">{highlight(String.truncate(result.description, 50), search)}</td>
        <td className="text-end text-nowrap d-table-cell d-md-none">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={3}>
            <ExpandedDetails row={result} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails row={result} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ row, search }: { row: GlobalSearchResult; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Search Result</div>
      <div className="p-1">
        <dl>
          <dt>Type</dt>
          <dd>{row.resultType}</dd>
          {row.description && (
            <>
              <dt>Description</dt>
              <dd>{highlight(row.description, search)}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
